import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { handleNonGlobalApiError } from "@/features/error/handleError";
import { interlude } from "@/interlude";

const EMAIL_LOCAL_PATTERN = /^[a-zA-Z0-9._%+-]+$/;
const FULL_EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface EditingProfile {
  id: number;
  name: string;
  fromEmail: string;
  replyTo: string;
}

interface SenderProfileFormModalProps {
  open: boolean;
  onClose: () => void;
  channelId: number;
  senderDomain: string;
  /**
   * Present → edit mode (pre-fill fields with this profile's values and
   * issue a PATCH on save). Absent → add mode (empty fields and POST a
   * new profile). The form, validation, and stub submit are otherwise
   * identical.
   */
  editingProfile?: EditingProfile | undefined;
  /**
   * Pre-fill value for Reply-To in **add** mode (usually the first
   * existing profile's reply address). Ignored in edit mode.
   */
  defaultReplyTo?: string | undefined;
}

const styles = {
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "12px",
    fontWeight: 500,
    color: theme.colors.staticFgBody,
  },
  required: {
    color: theme.colors.staticFgNegative,
    marginLeft: "2px",
  },
  emailRow: {
    display: "flex",
    alignItems: "stretch",
    gap: "0",
  },
  emailLocalInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  emailSuffix: {
    display: "flex",
    alignItems: "center",
    paddingX: "12px",
    fontSize: "13px",
    color: theme.colors.staticFgNote,
    backgroundColor: theme.colors.staticBgDisable,
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderLeft: "none",
    borderTopRightRadius: "4px",
    borderBottomRightRadius: "4px",
    whiteSpace: "nowrap",
  },
  hint: {
    fontSize: "11px",
    color: theme.colors.staticFgNote,
  },
  errorHint: {
    fontSize: "11px",
    color: theme.colors.staticFgNegative,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const splitEmailLocal = (email: string): string => {
  const at = email.indexOf("@");
  return at >= 0 ? email.slice(0, at) : email;
};

export const SenderProfileFormModal: React.FC<SenderProfileFormModalProps> =
  function SenderProfileFormModal({
    open,
    onClose,
    channelId,
    senderDomain,
    editingProfile,
    defaultReplyTo,
  }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const isEdit = editingProfile != null;

    const [name, setName] = useState(editingProfile?.name ?? "");
    const [emailLocal, setEmailLocal] = useState(() =>
      editingProfile ? splitEmailLocal(editingProfile.fromEmail) : "",
    );
    const [replyTo, setReplyTo] = useState(
      editingProfile?.replyTo ?? defaultReplyTo ?? "",
    );
    /**
     * Message for the two sender-profile errors the backend reports against
     * `from_email`. Both are mapped to `null` in `apiErrorCodeTranslationKeyMap`
     * so they surface on the field instead of as a global snackbar.
     */
    const [fromEmailApiError, setFromEmailApiError] = useState("");

    // Reset every time the modal opens so the form mirrors the latest
    // input (a freshly opened edit dialog must pre-fill its row, a
    // freshly opened add dialog must clear any stale draft).
    useEffect(() => {
      if (!open) return;
      setName(editingProfile?.name ?? "");
      setEmailLocal(
        editingProfile ? splitEmailLocal(editingProfile.fromEmail) : "",
      );
      setReplyTo(editingProfile?.replyTo ?? defaultReplyTo ?? "");
      setFromEmailApiError("");
    }, [open, editingProfile, defaultReplyTo]);

    const trimmedName = name.trim();
    const trimmedLocal = emailLocal.trim();
    const trimmedReplyTo = replyTo.trim();

    const emailLocalError = useMemo(() => {
      if (!trimmedLocal) return "";
      if (!EMAIL_LOCAL_PATTERN.test(trimmedLocal)) {
        return t(
          "channels.email.detail.addSenderProfile.email.invalid",
          "格式有誤，請僅填寫 @ 前方名稱",
        );
      }
      return "";
    }, [trimmedLocal, t]);

    const replyToError = useMemo(() => {
      if (!trimmedReplyTo) return "";
      if (!FULL_EMAIL_PATTERN.test(trimmedReplyTo)) {
        return t(
          "channels.email.detail.addSenderProfile.replyTo.invalid",
          "請輸入有效的 Email",
        );
      }
      return "";
    }, [trimmedReplyTo, t]);

    // Both errors the backend raises for a rejected profile are about
    // `from_email`, so they render under that field. `handleNonGlobalApiError`
    // returns false for globally-mapped codes, which `GlobalErrorHint` already
    // surfaces -- no fallback snackbar needed here.
    const handleSubmitError = useHandler(async (error: unknown) => {
      await handleNonGlobalApiError(error, {
        SENDER_PROFILE_ALREADY_EXISTS() {
          setFromEmailApiError(
            t(
              "channels.email.detail.senderProfile.error.emailInUse",
              "此寄件 email 已被使用，請改用其他前綴。",
            ),
          );
        },
        SENDER_PROFILE_DOMAIN_MISMATCH() {
          setFromEmailApiError(
            t(
              "channels.email.detail.senderProfile.error.domainMismatch",
              "寄件 email 必須使用已驗證的寄件網域。",
            ),
          );
        },
      });
    });

    // No explicit refetch: `injectAffectedApis` in @polifonia/zodios already
    // invalidates the mutation's own api namespace in `onSettled`, and both
    // these mutations and `useGetChannelSenderProfiles` live under `channel`.
    // Refetching here as well only cancels that in-flight request and issues a
    // duplicate.
    const handleSaved = useHandler((message: string) => {
      enqueueSnackbar(message, { variant: "success" });
      onClose();
    });

    const createMutation = interlude.channel.useCreateSenderProfile(
      { params: { channelId } },
      {
        onSuccess() {
          handleSaved(
            t(
              "channels.email.detail.addSenderProfile.success",
              "寄件設定檔已新增",
            ),
          );
        },
        onError: handleSubmitError,
      },
    );

    const updateMutation = interlude.channel.useUpdateSenderProfile(
      // Only fired in edit mode, where `editingProfile` is always present; the
      // fallback just keeps the hook call unconditional in add mode. It has to
      // satisfy the schema's positive-integer constraint even though it is
      // never sent, so it is 1 rather than 0.
      { params: { channelId, profileId: editingProfile?.id ?? 1 } },
      {
        onSuccess() {
          handleSaved(
            t(
              "channels.email.detail.editSenderProfile.success",
              "寄件設定檔已更新",
            ),
          );
        },
        onError: handleSubmitError,
      },
    );

    const submitting = createMutation.isPending || updateMutation.isPending;

    const canSubmit =
      !!trimmedName &&
      !!trimmedLocal &&
      !emailLocalError &&
      !!trimmedReplyTo &&
      !replyToError &&
      !submitting;

    const handleClose = () => {
      if (submitting) return;
      onClose();
    };

    const handleSubmit = useHandler(() => {
      if (!canSubmit) return;
      setFromEmailApiError("");
      const body = {
        name: trimmedName,
        fromEmail: `${trimmedLocal}@${senderDomain}`,
        replyTo: trimmedReplyTo,
      };
      // A PATCH with every field sent is still a valid partial update, and
      // sending them all keeps the two branches symmetric.
      if (editingProfile) updateMutation.mutate(body);
      else createMutation.mutate(body);
    });

    return (
      <Modal
        open={open}
        handleClose={handleClose}
        title={
          isEdit
            ? t(
                "channels.email.detail.editSenderProfile.title",
                "編輯寄件設定檔",
              )
            : t(
                "channels.email.detail.addSenderProfile.title",
                "新增寄件設定檔",
              )
        }
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button variant="plain" onClick={handleClose} disabled={submitting}>
              {t("channels.email.detail.addSenderProfile.cancel", "取消")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
            >
              {isEdit
                ? t("channels.email.detail.editSenderProfile.submit", "儲存")
                : t("channels.email.detail.addSenderProfile.submit", "新增")}
            </Button>
          </Stack>
        }
      >
        <Stack gap="16px">
          <Box sx={styles.field}>
            <Box component="span" sx={styles.label}>
              {t(
                "channels.email.detail.addSenderProfile.name.label",
                "寄件人名稱",
              )}
              <Box component="span" sx={styles.required}>
                *
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={t(
                "channels.email.detail.addSenderProfile.name.placeholder",
                "例如：漸強實驗室 技術支援",
              )}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
            />
          </Box>

          <Box sx={styles.field}>
            <Box component="span" sx={styles.label}>
              {t(
                "channels.email.detail.addSenderProfile.email.label",
                "寄件 Email",
              )}
              <Box component="span" sx={styles.required}>
                *
              </Box>
            </Box>
            <Box sx={styles.emailRow}>
              <TextField
                size="small"
                placeholder={t(
                  "channels.email.detail.addSenderProfile.email.placeholder",
                  "例如：support",
                )}
                value={emailLocal}
                onChange={(e) => {
                  setEmailLocal(e.target.value);
                  // The rejected value is being edited, so the server's verdict
                  // on it no longer applies.
                  setFromEmailApiError("");
                }}
                error={!!emailLocalError || !!fromEmailApiError}
                disabled={submitting}
                sx={styles.emailLocalInput}
              />
              <Box sx={styles.emailSuffix}>{`@${senderDomain}`}</Box>
            </Box>
            <Box
              component="span"
              sx={fromEmailApiError ? styles.errorHint : styles.hint}
            >
              {emailLocalError ||
                fromEmailApiError ||
                t(
                  "channels.email.detail.addSenderProfile.email.hint",
                  "只需填寫 @ 前方名稱，系統會自動組成完整寄件信箱。",
                )}
            </Box>
          </Box>

          <Box sx={styles.field}>
            <Box component="span" sx={styles.label}>
              {t(
                "channels.email.detail.addSenderProfile.replyTo.label",
                "Reply-To Email",
              )}
              <Box component="span" sx={styles.required}>
                *
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={t(
                "channels.email.detail.addSenderProfile.replyTo.placeholder",
                "例如：support@yourbrand.com",
              )}
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              error={!!replyToError}
              helperText={
                replyToError ||
                t(
                  "channels.email.detail.addSenderProfile.replyTo.hint",
                  "收件者回覆時，信件會寄送至此信箱。",
                )
              }
              disabled={submitting}
            />
          </Box>

          {isEdit ? null : (
            <Typography variant="note" color={theme.colors.staticFgNote}>
              {t(
                "channels.email.detail.addSenderProfile.note",
                "新增的寄件設定檔送出後，會建立於目前已驗證的寄件網域下。",
              )}
            </Typography>
          )}
        </Stack>
      </Modal>
    );
  };
